// libsignal high-level wrapper, now device-aware.
//
// Layer 2 of the No Rush envelope. The outer (TweetNaCl sealed-sender)
// layer in lib/crypto.ts hides the sender from the relay; this inner layer
// adds Signal's Double Ratchet to the message body, giving us:
//   - Forward secrecy: a one-time-future key compromise can't decrypt past
//     messages.
//   - Post-compromise security: after both sides exchange another message,
//     a one-time-past key compromise can't decrypt future messages either.
//   - Sender authentication via the device identity key.
//
// Linked-devices model: a libsignal session is per-(sender_device, recipient_device)
// pair. Senders fan out per recipient device; receivers handle envelopes
// addressed to their device id.

import libsignalInit, {
  FingerprintGenerator,
  KeyHelper,
  SessionBuilder,
  SessionCipher,
  SignalProtocolAddress,
  type DeviceType,
  type KeyPairType,
  type MessageType,
  type PreKeyPairType,
  type SignedPreKeyPairType,
} from '@privacyresearch/libsignal-protocol-typescript';

import type { EnvelopePayload, FriendDevice, Identity } from '../types';
import { openSealed, sealForRecipient } from './crypto';
import {
  countOwnOneTimePrekeys,
  fetchDeviceBundle,
  maxOneTimePrekeyId,
  postEnvelope,
  publishMoreOneTimePrekeys,
} from './relay';
import { SignalProtocolStore } from './signalStore';

// ----------------------------------------------------------------------------
// Library init.
// ----------------------------------------------------------------------------

let initPromise: Promise<void> | null = null;

export function ensureLibsignalReady(): Promise<void> {
  if (!initPromise) {
    initPromise = libsignalInit().then(() => undefined);
  }
  return initPromise;
}

// ----------------------------------------------------------------------------
// Address helpers. The libsignal "name" is the device's uuid; "deviceId"
// stays 1 because each install has one libsignal store locally. (The
// linked-devices abstraction lives at OUR layer, not libsignal's.)
// ----------------------------------------------------------------------------

const LIBSIGNAL_DEVICE_ID = 1;

export function addressFor(deviceId: string): SignalProtocolAddress {
  return new SignalProtocolAddress(deviceId, LIBSIGNAL_DEVICE_ID);
}

// ----------------------------------------------------------------------------
// Public bundle types
// ----------------------------------------------------------------------------

export interface PrekeyBundlePublic {
  registration_id: number;
  identity_pubkey_b64: string;
  signed_prekey_id: number;
  signed_prekey_pubkey_b64: string;
  signed_prekey_signature_b64: string;
}

export interface OneTimePrekeyPublic {
  key_id: number;
  pubkey_b64: string;
}

// ----------------------------------------------------------------------------
// b64 helpers
// ----------------------------------------------------------------------------

function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function b64ToBuf(s: string): ArrayBuffer {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

// ----------------------------------------------------------------------------
// Identity setup. Generates a fresh identity keypair, registration id,
// signed prekey, and a batch of one-time prekeys for THIS device.
// ----------------------------------------------------------------------------

export const DEFAULT_ONE_TIME_PREKEY_COUNT = 100;
export const SIGNED_PREKEY_INITIAL_ID = 1;
export const ONE_TIME_PREKEY_START_ID = 1;

export interface SetupResult {
  bundle: PrekeyBundlePublic;
  one_time_prekeys: OneTimePrekeyPublic[];
}

export async function setupSignalIdentity(args: {
  userId: string;
  store: SignalProtocolStore;
  count?: number;
}): Promise<SetupResult> {
  await ensureLibsignalReady();
  const count = args.count ?? DEFAULT_ONE_TIME_PREKEY_COUNT;

  const identityKeyPair: KeyPairType = await KeyHelper.generateIdentityKeyPair();
  const registrationId: number = KeyHelper.generateRegistrationId();
  await args.store.putIdentityKeyPair(identityKeyPair);
  await args.store.putLocalRegistrationId(registrationId);

  const signedPreKey: SignedPreKeyPairType = await KeyHelper.generateSignedPreKey(
    identityKeyPair,
    SIGNED_PREKEY_INITIAL_ID,
  );
  await args.store.storeSignedPreKey(SIGNED_PREKEY_INITIAL_ID, signedPreKey.keyPair);

  const oneTimePrekeys: PreKeyPairType[] = [];
  for (let i = 0; i < count; i++) {
    const id = ONE_TIME_PREKEY_START_ID + i;
    const pk = await KeyHelper.generatePreKey(id);
    await args.store.storePreKey(id, pk.keyPair);
    oneTimePrekeys.push(pk);
  }

  const bundle: PrekeyBundlePublic = {
    registration_id: registrationId,
    identity_pubkey_b64: bufToB64(identityKeyPair.pubKey),
    signed_prekey_id: SIGNED_PREKEY_INITIAL_ID,
    signed_prekey_pubkey_b64: bufToB64(signedPreKey.keyPair.pubKey),
    signed_prekey_signature_b64: bufToB64(signedPreKey.signature),
  };

  const one_time_prekeys: OneTimePrekeyPublic[] = oneTimePrekeys.map((opk) => ({
    key_id: opk.keyId,
    pubkey_b64: bufToB64(opk.keyPair.pubKey),
  }));

  return { bundle, one_time_prekeys };
}

export async function generateMoreOneTimePrekeys(args: {
  store: SignalProtocolStore;
  startId: number;
  count: number;
}): Promise<OneTimePrekeyPublic[]> {
  await ensureLibsignalReady();
  const out: OneTimePrekeyPublic[] = [];
  for (let i = 0; i < args.count; i++) {
    const id = args.startId + i;
    const pk = await KeyHelper.generatePreKey(id);
    await args.store.storePreKey(id, pk.keyPair);
    out.push({ key_id: id, pubkey_b64: bufToB64(pk.keyPair.pubKey) });
  }
  return out;
}

// ----------------------------------------------------------------------------
// Session establishment
// ----------------------------------------------------------------------------

export interface FetchedBundle {
  bundle: PrekeyBundlePublic;
  one_time_prekey?: OneTimePrekeyPublic;
}

export async function ensureSessionWithDevice(args: {
  store: SignalProtocolStore;
  recipientDeviceId: string;
  fetched: FetchedBundle;
}): Promise<void> {
  await ensureLibsignalReady();
  const address = addressFor(args.recipientDeviceId);

  const existing = await args.store.loadSession(address.toString());
  if (existing) return;

  const device: DeviceType = {
    identityKey: b64ToBuf(args.fetched.bundle.identity_pubkey_b64),
    signedPreKey: {
      keyId: args.fetched.bundle.signed_prekey_id,
      publicKey: b64ToBuf(args.fetched.bundle.signed_prekey_pubkey_b64),
      signature: b64ToBuf(args.fetched.bundle.signed_prekey_signature_b64),
    },
    preKey: args.fetched.one_time_prekey
      ? {
          keyId: args.fetched.one_time_prekey.key_id,
          publicKey: b64ToBuf(args.fetched.one_time_prekey.pubkey_b64),
        }
      : undefined,
    registrationId: args.fetched.bundle.registration_id,
  };

  const builder = new SessionBuilder(args.store, address);
  await builder.processPreKey(device);
}

// ----------------------------------------------------------------------------
// Encrypt / decrypt
// ----------------------------------------------------------------------------

export interface SignalCiphertext {
  type: number; // 1 = Whisper, 3 = PreKeyWhisper
  body_b64: string;
  registration_id?: number;
}

export async function encryptForDevice(args: {
  store: SignalProtocolStore;
  recipientDeviceId: string;
  plaintext: string;
}): Promise<SignalCiphertext> {
  await ensureLibsignalReady();
  const address = addressFor(args.recipientDeviceId);
  const cipher = new SessionCipher(args.store, address);
  const bytes = new TextEncoder().encode(args.plaintext);
  const msg: MessageType = await cipher.encrypt(bytes.buffer);

  const bin = msg.body ?? '';
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);

  return {
    type: msg.type,
    body_b64: bufToB64(u8.buffer),
    registration_id: msg.registrationId,
  };
}

export async function decryptFromDevice(args: {
  store: SignalProtocolStore;
  senderDeviceId: string;
  ciphertext: SignalCiphertext;
}): Promise<string | null> {
  await ensureLibsignalReady();
  const address = addressFor(args.senderDeviceId);
  const cipher = new SessionCipher(args.store, address);

  try {
    const bodyBuf = b64ToBuf(args.ciphertext.body_b64);
    let plaintextBuf: ArrayBuffer;
    if (args.ciphertext.type === 3) {
      plaintextBuf = await cipher.decryptPreKeyWhisperMessage(bodyBuf, 'binary');
    } else {
      plaintextBuf = await cipher.decryptWhisperMessage(bodyBuf, 'binary');
    }
    return new TextDecoder().decode(plaintextBuf);
  } catch (err) {
    console.error('[no-rush] signal decrypt error:', err);
    return null;
  }
}

// ----------------------------------------------------------------------------
// Safety number (device-pair fingerprint)
// ----------------------------------------------------------------------------

export async function signalSafetyNumber(args: {
  localDeviceId: string;
  localIdentityKey: ArrayBuffer;
  remoteDeviceId: string;
  remoteIdentityKey: ArrayBuffer;
}): Promise<string> {
  await ensureLibsignalReady();
  const gen = new FingerprintGenerator(5200);
  return gen.createFor(
    args.localDeviceId,
    args.localIdentityKey,
    args.remoteDeviceId,
    args.remoteIdentityKey,
  );
}

// ----------------------------------------------------------------------------
// Per-user singleton store cache
// ----------------------------------------------------------------------------

let _storeCache: { userId: string; store: SignalProtocolStore } | null = null;

export function getSignalStore(userId: string): SignalProtocolStore {
  if (!_storeCache || _storeCache.userId !== userId) {
    _storeCache = { userId, store: new SignalProtocolStore(userId) };
  }
  return _storeCache.store;
}

export function clearSignalStoreCache(): void {
  _storeCache = null;
}

export async function hasSignalIdentity(store: SignalProtocolStore): Promise<boolean> {
  const kp = await store.getIdentityKeyPair();
  return !!kp;
}

// ----------------------------------------------------------------------------
// Two-layer envelope: TweetNaCl outer (sealed sender, per-device wrap)
// composing Signal inner (Double Ratchet, per-device session).
// ----------------------------------------------------------------------------

async function hasSessionWithDevice(
  store: SignalProtocolStore,
  recipientDeviceId: string,
): Promise<boolean> {
  const session = await store.loadSession(addressFor(recipientDeviceId).toString());
  return !!session;
}

/**
 * Encrypt for a SPECIFIC recipient device and return the envelope blob.
 * Senders that want to deliver a message to a user's "account" call this
 * once per device in friend.devices.
 *
 * The returned ciphertext is opaque to the relay. The TweetNaCl outer is
 * encrypted to the recipient device's outer_pubkey; the Signal inner is
 * encrypted to the recipient device's libsignal identity.
 */
export async function encryptAndSeal(args: {
  me: Identity;
  recipientDevice: FriendDevice;
  payload: EnvelopePayload;
}): Promise<string> {
  const store = getSignalStore(args.me.id);

  // Make sure we have a session with this specific device.
  let haveSession = await hasSessionWithDevice(store, args.recipientDevice.id);
  if (!haveSession) {
    const fetched = await fetchDeviceBundle(args.recipientDevice.id);
    if (!fetched) {
      // The device row disappeared between cache + send (unlinked). Throw
      // so the caller can surface the friendly "device gone" message.
      throw new Error(
        "That device isn't on No Rush anymore — it may have been unlinked.",
      );
    }
    await ensureSessionWithDevice({
      store,
      recipientDeviceId: args.recipientDevice.id,
      fetched: {
        bundle: fetched.bundle,
        one_time_prekey: fetched.one_time_prekey,
      },
    });
    haveSession = true;
  }

  // Signal-encrypt the JSON payload.
  const cipher = await encryptForDevice({
    store,
    recipientDeviceId: args.recipientDevice.id,
    plaintext: JSON.stringify(args.payload),
  });

  // Wrap in a thin outer stub addressed to the recipient device.
  const stub: EnvelopePayload = {
    sender_username: '',
    body: '',
    sent_at: '',
    signal: {
      sender_device_id: args.me.device_id,
      cipher,
    },
  };
  return sealForRecipient(stub, args.recipientDevice.outer_pubkey_b64);
}

/**
 * Inverse of encryptAndSeal — unseals the outer with this device's
 * private key and Signal-decrypts the inner from the sender's device id.
 * Returns null on any failure.
 */
export async function unsealAndDecrypt(args: {
  me: Identity;
  ciphertext_b64: string;
}): Promise<EnvelopePayload | null> {
  const outer = openSealed(args.ciphertext_b64, args.me.outer_seckey_b64);
  if (!outer) return null;
  if (!outer.signal) return outer; // legacy path (shouldn't happen post-cutover)

  const store = getSignalStore(args.me.id);
  const innerText = await decryptFromDevice({
    store,
    senderDeviceId: outer.signal.sender_device_id,
    ciphertext: outer.signal.cipher,
  });
  if (innerText == null) return null;

  try {
    return JSON.parse(innerText) as EnvelopePayload;
  } catch {
    return null;
  }
}

/**
 * Encrypt + post a payload to every device in the list. The caller passes
 * the union of all devices to send to — for a 1:1 message that's
 * friend.devices; for a group message it's every member's devices flattened.
 * Posts run in parallel.
 *
 * Errors on individual devices propagate. If a single recipient device
 * was unlinked between the cached lookup and the send, the friendly
 * "device isn't on No Rush anymore" message bubbles up.
 */
export async function sendToDevices(args: {
  me: Identity;
  recipientDevices: FriendDevice[];
  payload: EnvelopePayload;
  /** Optional future delivery time. Omit/undefined = deliver immediately. */
  deliverAt?: Date | null;
}): Promise<void> {
  const deliver_at = args.deliverAt ? args.deliverAt.toISOString() : undefined;
  await Promise.all(
    args.recipientDevices.map(async (device) => {
      const ciphertext_b64 = await encryptAndSeal({
        me: args.me,
        recipientDevice: device,
        payload: args.payload,
      });
      return postEnvelope({
        recipient_device_id: device.id,
        ciphertext_b64,
        deliver_at,
      });
    }),
  );
}

// ----------------------------------------------------------------------------
// One-time prekey replenishment for this device
// ----------------------------------------------------------------------------

const REPLENISH_THRESHOLD = 20;
const REPLENISH_BATCH_SIZE = 50;

export async function replenishOneTimePrekeysIfLow(args: {
  userId: string;
  deviceId: string;
  threshold?: number;
  batchSize?: number;
}): Promise<{ generated: number; remaining: number }> {
  const threshold = args.threshold ?? REPLENISH_THRESHOLD;
  const batchSize = args.batchSize ?? REPLENISH_BATCH_SIZE;

  const remaining = await countOwnOneTimePrekeys(args.deviceId);
  if (remaining >= threshold) return { generated: 0, remaining };

  const maxId = await maxOneTimePrekeyId(args.deviceId);
  const store = getSignalStore(args.userId);
  const fresh = await generateMoreOneTimePrekeys({
    store,
    startId: maxId + 1,
    count: batchSize,
  });
  await publishMoreOneTimePrekeys(args.deviceId, fresh);
  return { generated: fresh.length, remaining: remaining + fresh.length };
}

// ----------------------------------------------------------------------------
// Test helpers
// ----------------------------------------------------------------------------

export const __test_internal = {
  bufToB64,
  b64ToBuf,
};
